import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { searchItems } from "../utils/itemSearchApi";
import useDebouncedValue from "./useDebouncedValue";

export const MIN_QUERY_LENGTH = 2;
export const MAX_RESULTS = 10;

// Debounced item search. Each new query aborts the previous request, so an
// older (slower) response can never overwrite a newer one.
export default function useItemSearch(customerId, query) {
  const debouncedQuery = useDebouncedValue(query.trim(), 300);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [retryKey, setRetryKey] = useState(0);
  const controllerRef = useRef(null);

  useEffect(() => {
    controllerRef.current?.abort();

    if (!customerId || debouncedQuery.length < MIN_QUERY_LENGTH) {
      setItems([]);
      setLoading(false);
      setError(null);
      return undefined;
    }

    const controller = new AbortController();
    controllerRef.current = controller;
    setLoading(true);
    setError(null);

    searchItems(customerId, debouncedQuery, { signal: controller.signal })
      .then((results) => {
        setItems(results.slice(0, MAX_RESULTS));
        setLoading(false);
      })
      .catch((err) => {
        if (axios.isCancel(err) || err?.name === "CanceledError") return;
        setItems([]);
        setError(err?.response?.data?.message || "Could not load items. Please try again.");
        setLoading(false);
      });

    return () => controller.abort();
  }, [customerId, debouncedQuery, retryKey]);

  const retry = useCallback(() => setRetryKey((k) => k + 1), []);

  return {
    items,
    loading,
    error,
    retry,
    searchedQuery: debouncedQuery,
    isTyping: query.trim() !== debouncedQuery,
  };
}
